// src/modals/ReportModal.js
import React, { useState } from 'react';
import { X, Flag } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useModal } from '../hooks';
import reportService from '../services/reportService';
import { Btn, Select } from '../components/ui';
import PajNtaubBand from '../components/ui/PajNtaubBand';

const PROFILE_REASONS = [
  { value: 'inappropriate', labelKey: 'reportModal.reasons.inappropriate' },
  { value: 'fake_profile', labelKey: 'reportModal.reasons.fake_profile' },
  { value: 'harassment', labelKey: 'reportModal.reasons.harassment' },
  { value: 'spam', labelKey: 'reportModal.reasons.spam' },
  { value: 'other', labelKey: 'reportModal.reasons.other' },
];

const ANNONCE_REASONS = [
  { value: 'inappropriate', labelKey: 'reportModal.reasonsAnnonce.inappropriate' },
  { value: 'scam', labelKey: 'reportModal.reasonsAnnonce.scam' },
  { value: 'spam', labelKey: 'reportModal.reasonsAnnonce.spam' },
  { value: 'wrong_category', labelKey: 'reportModal.reasonsAnnonce.wrong_category' },
  { value: 'illegal', labelKey: 'reportModal.reasonsAnnonce.illegal' },
  { value: 'other', labelKey: 'reportModal.reasonsAnnonce.other' },
];

const ReportModal = ({
  isOpen,
  onClose,
  reportedId,
  reportedName,
  currentUserId,
  toast,
  type = 'profile',
  annonceId,
}) => {
  const { onBackdrop } = useModal(isOpen, onClose);
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);

  if (!isOpen) return null;

  const isAnnonce = type === 'annonce';
  const rawReasons = isAnnonce ? ANNONCE_REASONS : PROFILE_REASONS;
  const reasons = rawReasons.map((r) => ({ value: r.value, label: t(r.labelKey) }));
  const title = isAnnonce
    ? t('reportModal.titleAnnonce')
    : t('reportModal.titleProfile', { name: reportedName || '' });
  const alreadyMsg = isAnnonce ? t('reportModal.alreadyAnnonce') : t('reportModal.alreadyProfile');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason) return;

    setSending(true);
    let r;
    if (isAnnonce && annonceId) {
      r = await reportService.createAnnonceReport(
        currentUserId,
        annonceId,
        reportedId,
        reason,
        comment
      );
    } else {
      r = await reportService.createReport(currentUserId, reportedId, reason, comment);
    }
    setSending(false);

    if (r.success) {
      toast && toast(t('reportModal.toastSuccess'), 'success');
      setReason('');
      setComment('');
      onClose();
    } else if (r.error === 'already_reported') {
      toast && toast(alreadyMsg, 'warning');
      onClose();
    } else {
      toast && toast(t('reportModal.toastError'), 'error');
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4 animate-fade-in"
      onClick={onBackdrop}
    >
      <div
        data-testid="report-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="bg-white dark:bg-gray-900 w-full sm:max-w-md rounded-t-2xl sm:rounded-xl max-h-[85dvh] overflow-hidden animate-slide-up"
        style={{ overscrollBehavior: 'contain' }}
      >
        <PajNtaubBand height={4} />
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(85dvh - 4px)' }}>
          <div className="flex items-center justify-between p-4 border-b dark:border-gray-800">
            <div className="flex items-center gap-2">
              <Flag size={18} className="text-rose-500" />
              <h3 className="text-base font-semibold dark:text-gray-100">{title}</h3>
            </div>
            <button
              onClick={onClose}
              aria-label={t('reportModal.ariaClose')}
              className="w-8 h-8 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center transition"
            >
              <X size={16} className="text-gray-500" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            <Select
              label={t('reportModal.reasonLabel')}
              options={reasons}
              placeholder={t('reportModal.reasonPlaceholder')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              data-testid="report-reason"
            />

            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                {t('reportModal.commentLabel')}{' '}
                <span className="font-normal text-gray-400">
                  {t('reportModal.commentOptional')}
                </span>
              </label>
              <textarea
                data-testid="report-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value.slice(0, 500))}
                placeholder={t('reportModal.commentPlaceholder')}
                rows={3}
                maxLength={500}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700 focus:border-rose-300 dark:focus:border-rose-500 rounded-xl dark:text-gray-100 resize-none"
              />
              <p className="text-xs text-gray-400 text-right">{comment.length}/500</p>
            </div>

            <Btn
              type="submit"
              className="w-full text-sm py-2.5"
              loading={sending}
              disabled={!reason}
              data-testid="report-submit"
            >
              {t('reportModal.submitBtn')}
            </Btn>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ReportModal;
